import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import axiosInstance from "../axios/axios";
import Loader from "./Loader";
import { useTranslate } from "./TranslationText";
import NetaqInput from "./NetaqInput";
import { convertToCamelCase } from "../helper/convertToCamelCase";
import EahsForm from "./EahsForm";
import NewActionButton from "./ActionButtons/NewActionButton";
import { Link } from "react-router-dom";
import { formatDateTimeToString } from "../helper/formatDateTimeToString";
import { eformInput, noDataCard } from "../common/cssclasses";

const PAGE_SIZE = 20;

function toApiDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.format("YYYY-MM-DD");
}

export default function PaymentHistory() {
  const { translate } = useTranslate();

  const [currentPayments, setCurrentPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paymentLoaded, setPaymentLoaded] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Filter states
  const [referenceNumber, setReferenceNumber] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [paidStatus, setPaidStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});

  async function fetchPayments(page = 1, filters = {}) {
    setPaymentLoaded(false);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("take", PAGE_SIZE);
      if (filters.refranceNumber) params.append("refranceNumber", filters.refranceNumber);
      if (filters.from)           params.append("from", filters.from);
      if (filters.to)             params.append("to", filters.to);
      if (filters.status)         params.append("status", filters.status);

      const response = await axiosInstance.get(
        `payment/getpaymenttransactionbyuserid?${params.toString()}`
      );

      if (response.data.responseCode === "200") {
        setCurrentPayments(response.data.result ?? []);
        const total =
          response.data.paginationInfo?.totalCount ??
          response.data.totalCount ??
          (response.data.result?.length ?? 0);
        setTotalCount(total);
      } else {
        setCurrentPayments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading payment history", error);
      setCurrentPayments([]);
      setTotalCount(0);
    } finally {
      setPaymentLoaded(true);
    }
  }

  const buildFilters = (refNum, from, to, status) => ({
    refranceNumber: refNum?.trim() || undefined,
    from:   toApiDate(from)  || undefined,
    to:     toApiDate(to)    || undefined,
    status: status           || undefined,
  });

  useEffect(() => {
    fetchPayments(1, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    const filters = buildFilters(referenceNumber, fromDate, toDate, paidStatus);
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchPayments(1, filters);
  };

  const handleReset = () => {
    setReferenceNumber("");
    setFromDate(null);
    setToDate(null);
    setPaidStatus("");
    setAppliedFilters({});
    setCurrentPage(1);
    fetchPayments(1, {});
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchPayments(newPage, appliedFilters);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <div className="col-lg-12 col-md-12 col-sm-12">

        {/* Filter controls */}
        <EahsForm className={'mb-main'} title={translate("web.paymentHistory", "Payment History")}>
          <div className={eformInput}>
            <NetaqInput
              type={"text"}
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              label={translate("web.refranceNumber", "Refrance Number")}
              placeholder={translate("web.refranceNumber", "Refrance Number")}
              name={translate("web.refranceNumber", "Refrance Number")}
            />
          </div>
          <div className={eformInput}>
            <NetaqInput
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e)}
              label={translate("web.from", "From ")}
              name={translate("web.from", "From ")}
              maxDate={toDate || dayjs()}
            />
          </div>
          <div className={eformInput}>
            <NetaqInput
              type="date"
              minDate={fromDate}
              value={toDate}
              onChange={(e) => setToDate(e)}
              label={translate("web.to", "To ")}
              name={translate("web.to", "To ")}
              maxDate={dayjs()}
            />
          </div>

          <div className={eformInput}>
            <label className="capatalize_text">
              {translate("web.status", "Status")}
            </label>
            <select
              className="form-control"
              value={paidStatus}
              onChange={(e) => setPaidStatus(e.target.value)}
            >
              <option value="">{translate("web.all", "All")}</option>
              <option value="paid">{translate("web.completed", "Completed")}</option>
              <option value="notPaid">{translate("web.notCompleted", "Not Completed")}</option>
            </select>
          </div>
          <div className="col-lg-2 col-md-3 col-sm-12 ms-auto" onClick={handleReset}>
            <NewActionButton
              classNames="btn-outline-secondary"
              isButton
              type={'reset'}
              text={translate("web.reset", "Reset")}
            />
          </div>
          <div className="col-lg-2 col-md-2 col-sm-12" onClick={handleSubmit}>
            <NewActionButton
              classNames="btn-danger"
              isButton
              type={'submit'}
              text={translate("web.search", "Search")}
            />
          </div>
        </EahsForm>

        {/* Table area — Loader shown only here, keeping filter section visible like ParticipateHistory */}
        {paymentLoaded ? (
          currentPayments.length > 0 ? (
            <div className="scrolableTable">
              <table className="table table-striped table-bordered rtl horseResultTable mb-main">
                <thead className="theadHorseSelect">
                  <tr>
                    <th className="text-center">{translate("web.refranceNumber", "Refrance Number")}</th>
                    <th className="text-center">{translate("web.date", "Date")}</th>
                    <th className="text-center">{translate("web.amount", "Amount")}</th>
                    <th className="text-center">{translate("web.paymentReason", "Payment Reason")}</th>
                    <th className="text-center">{translate("web.status", "Status")}</th>
                    <th className="text-center">{translate("web.bankDecision", "Bank Decision")}</th>
                    <th className="text-center">{translate("web.paymentType", "Payment Type")}</th>
                    <th className="text-center">{translate("web.receiptPerformaInvoice", "Receipt - Performa Invoice")}</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {currentPayments.map((item, index) => (
                    <tr key={index}>
                      <td className="rtl">{item.refranceNumber}</td>
                      <td className="rtl">{formatDateTimeToString(item.createdDate)}</td>
                      <td className="rtl">
                        {item?.amount?.toLocaleString("en-US") + " " + item.currency}
                      </td>
                      <td className="rtl">
                        {translate(
                          `web.Payment${convertToCamelCase(item.paymentType)}`,
                          convertToCamelCase(item.paymentType)
                        )}
                      </td>
                      <td className={`rtl text-center ${item.isPaid ? "text-success" : "text-danger"}`}>
                        {item.isPaid
                          ? translate("web.completed", "Completed")
                          : translate("web.notCompleted", "Not Completed")}
                      </td>
                      <td className="rtl" style={{ maxWidth: 75 }}>
                        {item.decisionText}{" "}
                        {item?.decisionText?.toLowerCase() !== "accept" && (
                          <>{item?.messageText}</>
                        )}
                      </td>
                      <td className="rtl" style={{ maxWidth: 75 }}>
                        {item.paymentMethod == null || item.paymentMethod === "Online"
                          ? translate("web.onlinePayment", "Online Payment")
                          : translate("web.creditPayment", "Credit Payment")}
                      </td>
                      <td className="rtl">
                        {!!item?.receiptUrl && (
                          <Link className="btn btn-danger my-1 w-100" target="_blank" to={item?.receiptUrl}>
                            <i className="fa-solid fa-download"></i> {translate('web.recipt', 'Recipt')}
                          </Link>
                        )}
                        {!!item?.vatUrl && (
                          <Link className="btn btn-danger my-1 w-100" target="_blank" to={item?.vatUrl}>
                            <i className="fa-solid fa-receipt"></i>
                            {translate('web.vatInvoice', 'VAT Invoice')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination buttons — same UI as before */}
              <div className="pagination-buttons rtl">
                <NewActionButton
                  classNames="btn-outline-danger w-unset"
                  key={'prev'}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  text={translate("web.previous", "Previous")}
                />
                <NewActionButton
                  classNames="btn-danger w-unset ms-2"
                  key={'pages'}
                  text={`${currentPage} / ${totalPages}`}
                />
                <NewActionButton
                  key={'next'}
                  classNames="btn-outline-danger w-unset ms-2"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  text={translate("web.next", "Next")}
                />
              </div>
            </div>
          ) : (
            <div className={noDataCard}>
              {translate("web.nodataFound", "No Result Found")}
            </div>
          )
        ) : (
          <Loader />
        )}
      </div>
    </>
  );
}
